import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import { completeConnect, HrcApiError, pollConnectStatus, startConnect } from '../api/hrcClient'
import { toConnectionPayload } from '../connectionPayload'
import type { HrcConnectCompleteRequest, HrcConnectStart, HrcPollState, StitchConnectionData } from '../types'

/** What went wrong, and therefore which recovery the UI may offer. */
export type HrcFlowError =
  /** /start failed: safe to call start again. */
  | { kind: 'start-failed' }
  /** Fasten could not resolve its widget configuration: nothing was sent; start with a fresh handoff. */
  | { kind: 'widget-config' }
  /** Widget completed without a usable connection: nothing was sent; the user must run the widget again. */
  | { kind: 'no-connection' }
  /** /complete lost, throttled, unauthorized-for-now or 5xx: re-send the SAME request (same key). Never relaunch. */
  | { kind: 'retryable' }
  /** /complete got a 2xx we cannot verify as ACTIVE: the server may have saved it. Same-key retry only. */
  | { kind: 'unconfirmed' }
  /** The server is still saving this very request (in-progress, or ANY 409 on a same-key replay). Same-key retry only. */
  | { kind: 'in-progress'; retryAfterSeconds: number | null }
  /** The EHR vendor is unavailable or returned bad data and nothing was written (VENDOR_ERROR / VENDOR_UNCONFIRMED). Same-key retry only. */
  | { kind: 'vendor-unavailable' }
  /** RATE_LIMITED / 408 / 425 / 429. Same-key retry only. */
  | { kind: 'rate-limited'; retryAfterSeconds: number | null }
  /** The BFF says the session is gone (UNAUTHENTICATED). `resume` says what "Try again" re-does. */
  | { kind: 'session-expired'; resume: 'retry' | 'start' }
  /** The request cannot succeed (expired, already used under another key, forbidden, invalid). */
  | { kind: 'fatal'; reason: 'expired' | 'conflict' | 'revoked' | 'other' }

export type HrcConnectState =
  | { phase: 'idle' }
  | { phase: 'starting' }
  | { phase: 'ready'; session: HrcConnectStart }
  /** The widget was closed before any completion. Nothing was sent. Not an error. */
  | { phase: 'closed' }
  | { phase: 'submitting' }
  /**
   * Settling, not failing. Fasten's webhook lands ~30s AFTER the widget closes, so the first
   * complete reliably comes back `VENDOR_UNCONFIRMED`. That is the expected sequence, not an
   * error, and the user must not be asked to act on it (nor shown a red alert).
   */
  | { phase: 'waiting'; attempt: number }
  /** `additionalConnections`: widget-reported connections beyond the first that were NOT forwarded. */
  | { phase: 'connected'; additionalConnections: number }
  | { phase: 'error'; error: HrcFlowError }

/** `sends` counts how many times this exact request has left the browser (a replay is any send after the first). */
type PendingComplete = { request: HrcConnectCompleteRequest; idempotencyKey: string; sends: number }

/**
 * How often to ask the server whether the health system has confirmed yet. The poll is a cheap
 * row read, so the cost of asking is close to nothing; the interval is set by how long a family
 * user should stare at a spinner after the work is already done, not by what the server can bear.
 */
const POLL_INTERVAL_MS = 5_000

/**
 * How long to keep asking. Sized against a measured confirmation lag that has grown 26s -> 31s
 * -> 35s across Dev runs, and reached about 2 minutes in a local run (FAM-262), so there is room
 * for it to keep drifting.
 *
 * Reaching the end is NOT an error: the poll only decides WHEN to enroll, never WHETHER we may.
 * There are states the server cannot see from this handoff alone (an enrollment that already
 * succeeded under a different one), so an exhausted budget sends the enroll anyway and lets its
 * real answer — success or a genuine failure — be the one the user sees.
 */
const POLL_BUDGET_MS = 120_000

/**
 * ONLY the vendor-confirmation wait resumes polling. `VENDOR_UNCONFIRMED`/`VENDOR_ERROR` mean the
 * handle is not yet proven and nothing was written — the expected confirmation lag, which the
 * family user should never be asked to act on.
 *
 * Deliberately NOT included: `in-progress` and `unconfirmed`. Those mean the server may ALREADY
 * have committed this request, which is a different situation with a different honest answer, and
 * CONTRACT_V4 R12/R13 pins the message and the single offered action for each. Widening this
 * predicate silently rewrites those contracts.
 */
function isSettling(error: HrcFlowError): boolean {
  return error.kind === 'vendor-unavailable'
}

function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

/**
 * Stage-1 consent flow. Everything here is in-memory only (refs/state): the SPA persists nothing and
 * never sees a token or FHIR URL. The handoff/nonce are opaque values relayed between BFF and widget.
 */
export function useHrcConnect(recordSubjectPrincipalId: string | undefined) {
  const client = useFamilyPortalApiClient()
  const [state, setState] = useState<HrcConnectState>({ phase: 'idle' })
  const sessionRef = useRef<HrcConnectStart | null>(null)
  const pendingRef = useRef<PendingComplete | null>(null)
  const inFlightRef = useRef(false)
  const completionSeenRef = useRef(false)
  /** Lower-cased ids of DISTINCT valid connections seen other than the forwarded one. */
  const otherConnectionIdsRef = useRef<Set<string>>(new Set())
  const [malformedSeen, setMalformedSeen] = useState(false)
  const mountedRef = useRef(true)
  /** Polls taken in the CURRENT wait; reset by success, start, and a real error. */
  const attemptRef = useRef(0)
  const waitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** When the CURRENT wait began; the budget is measured from here, not from each poll. */
  const waitStartedAtRef = useRef(0)
  /** Breaks the submit <-> poll cycle without making either depend on itself. */
  const submitRef = useRef<(pending: PendingComplete) => Promise<void>>(async () => {})
  const pollRef = useRef<() => Promise<void>>(async () => {})

  const cancelWait = useCallback(() => {
    if (waitTimerRef.current !== null) {
      clearTimeout(waitTimerRef.current)
      waitTimerRef.current = null
    }
    attemptRef.current = 0
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      // A timer surviving unmount would re-send against a session the user has left.
      if (waitTimerRef.current !== null) clearTimeout(waitTimerRef.current)
    }
  }, [])

  const update = useCallback((next: HrcConnectState) => {
    if (mountedRef.current) setState(next)
  }, [])

  const start = useCallback(async () => {
    if (!recordSubjectPrincipalId || inFlightRef.current) return
    inFlightRef.current = true
    // Start over abandons the old handoff: a pending re-send must not fire against it.
    cancelWait()
    sessionRef.current = null
    pendingRef.current = null
    completionSeenRef.current = false
    otherConnectionIdsRef.current = new Set()
    setMalformedSeen(false)
    update({ phase: 'starting' })
    try {
      // No client-side expiry check: the only clock available here is the browser's, which may be wrong, and a
      // false "expired" would lock the user out. The server's 410 is the authority (CONTRACT_V3 R6).
      const session = await startConnect(client, recordSubjectPrincipalId)
      sessionRef.current = session
      update({ phase: 'ready', session })
    } catch (err) {
      const sessionGone = err instanceof HrcApiError && err.kind === 'session'
      update({
        phase: 'error',
        error: sessionGone ? { kind: 'session-expired', resume: 'start' } : { kind: 'start-failed' },
      })
    } finally {
      inFlightRef.current = false
    }
  }, [cancelWait, client, recordSubjectPrincipalId, update])

  const submit = useCallback(
    async (pending: PendingComplete) => {
      inFlightRef.current = true
      pending.sends += 1
      // Only the FIRST send shows "submitting"; a scheduled re-send keeps the waiting state so the
      // card does not flicker between two spinners.
      if (attemptRef.current === 0) update({ phase: 'submitting' })
      /**
       * Route the outcome. A settling outcome re-sends the SAME request and key on the schedule and
       * never reaches the user; anything else is a real result they must see. Exhausting the
       * schedule falls through to the ordinary error state.
       */
      const fail = (error: HrcFlowError) => {
        // The confirmation still has not landed. Nothing was written, so go back to waiting
        // rather than asking the user to act — but only while the budget lasts, and only if we
        // are still mounted: arming a timer after cleanup would leak one that nothing cancels.
        if (isSettling(error) && mountedRef.current && Date.now() - waitStartedAtRef.current < POLL_BUDGET_MS) {
          update({ phase: 'waiting', attempt: attemptRef.current })
          waitTimerRef.current = setTimeout(() => {
            waitTimerRef.current = null
            if (mountedRef.current) void pollRef.current()
          }, POLL_INTERVAL_MS)
          return
        }
        cancelWait()
        update({ phase: 'error', error })
      }
      try {
        await completeConnect(client, pending.request, pending.idempotencyKey)
        pendingRef.current = null
        sessionRef.current = null
        const additionalConnections = otherConnectionIdsRef.current.size
        if (additionalConnections > 0) {
          // Count-only breadcrumb: no ids, no payload.
          Sentry.addBreadcrumb({
            category: 'hrc',
            level: 'warning',
            message: 'additional Fasten connections were not forwarded',
            data: { additionalConnections },
          })
        }
        cancelWait()
        update({ phase: 'connected', additionalConnections })
      } catch (err) {
        if (err instanceof HrcApiError && err.kind === 'unconfirmed') {
          fail({ kind: 'unconfirmed' })
        } else if (
          err instanceof HrcApiError &&
          (err.kind === 'in-progress' || (err.kind === 'conflict' && pending.sends > 1))
        ) {
          // A same-key replay never genuinely conflicts (CONTRACT item 1), and an earlier send may have reached
          // the server: a 409 here means "still being saved", not "already used". Never offer Start over.
          fail({ kind: 'in-progress', retryAfterSeconds: err.retryAfterSeconds })
        } else if (err instanceof HrcApiError && err.kind === 'vendor') {
          fail({ kind: 'vendor-unavailable' })
        } else if (err instanceof HrcApiError && err.kind === 'rate-limited') {
          fail({ kind: 'rate-limited', retryAfterSeconds: err.retryAfterSeconds })
        } else if (err instanceof HrcApiError && err.kind === 'session') {
          fail({ kind: 'session-expired', resume: 'retry' })
        } else if (err instanceof HrcApiError && !err.retryable) {
          pendingRef.current = null
          const reason =
            err.kind === 'expired'
              ? 'expired'
              : err.kind === 'conflict'
                ? 'conflict'
                : err.kind === 'revoked'
                  ? 'revoked'
                  : 'other'
          fail({ kind: 'fatal', reason })
        } else {
          fail({ kind: 'retryable' })
        }
      } finally {
        inFlightRef.current = false
      }
    },
    [cancelWait, client, update]
  )
  submitRef.current = submit

  /**
   * One ask, then either enroll or wait again. Runs immediately when the widget closes — the
   * confirmation has sometimes already landed by then — and every {@link POLL_INTERVAL_MS} after.
   *
   * The poll never decides that the user IS connected: `CONNECTED` still goes through /complete,
   * whose `status === 'ACTIVE'` is the only proof this app accepts, and which also finishes any
   * bookkeeping an interrupted earlier attempt left behind.
   */
  const poll = useCallback(async () => {
    const pending = pendingRef.current
    if (!pending || !mountedRef.current) return
    attemptRef.current += 1

    let state: HrcPollState
    try {
      state = await pollConnectStatus(client, pending.request.handoff)
    } catch (err) {
      if (err instanceof HrcApiError && err.kind === 'session') {
        cancelWait()
        update({ phase: 'error', error: { kind: 'session-expired', resume: 'retry' } })
        return
      }
      // Anything else is "ask again": a poll that did not answer has changed nothing, and the
      // budget already bounds how long we can go on not knowing.
      state = 'UNKNOWN'
    }
    if (!mountedRef.current) return

    if (state === 'GONE') {
      pendingRef.current = null
      cancelWait()
      update({ phase: 'error', error: { kind: 'fatal', reason: 'expired' } })
      return
    }
    // Budget spent: send it anyway. The poll is an optimization, never a gate — there are
    // successful outcomes it cannot see, so its silence must not be able to block an enroll.
    if (state === 'READY' || state === 'CONNECTED' || Date.now() - waitStartedAtRef.current >= POLL_BUDGET_MS) {
      await submitRef.current(pending)
      return
    }
    update({ phase: 'waiting', attempt: attemptRef.current })
    waitTimerRef.current = setTimeout(() => {
      waitTimerRef.current = null
      if (mountedRef.current) void pollRef.current()
    }, POLL_INTERVAL_MS)
  }, [cancelWait, client, update])
  pollRef.current = poll

  const onWidgetComplete = useCallback(
    (entries: StitchConnectionData[]) => {
      const session = sessionRef.current
      if (!session) return
      // Only entries with a usable org_connection_id are connections; the rest are neither forwarded nor "losses".
      const valid = entries.map(toConnectionPayload).filter((c): c is NonNullable<typeof c> => c !== null)
      const idKey = (id: string) => id.toLowerCase()

      // A completion is already being handled (or awaiting a same-key retry). The first one wins and is never
      // re-built from later metadata; a DIFFERENT valid connection is remembered so it is surfaced, not lost.
      const pending = pendingRef.current
      if (pending || inFlightRef.current) {
        const forwarded = pending ? idKey(pending.request.connection.orgConnectionId) : undefined
        for (const c of valid)
          if (idKey(c.orgConnectionId) !== forwarded) otherConnectionIdsRef.current.add(idKey(c.orgConnectionId))
        return
      }

      completionSeenRef.current = true
      // Forward the FIRST VALID entry (spec: data[0], but an empty data[0] must not hide a usable data[1]).
      const connection = valid[0]
      if (!connection) {
        // Contract item 9: no org_connection_id => never call the backend.
        update({ phase: 'error', error: { kind: 'no-connection' } })
        return
      }
      for (const c of valid.slice(1)) {
        if (idKey(c.orgConnectionId) !== idKey(connection.orgConnectionId)) {
          otherConnectionIdsRef.current.add(idKey(c.orgConnectionId))
        }
      }
      const next: PendingComplete = {
        request: { handoff: session.handoff, connection },
        idempotencyKey: newIdempotencyKey(),
        sends: 0,
      }
      pendingRef.current = next
      // Ask before enrolling. The health system confirms to the server ~30s after the window
      // closes, and an enroll sent before that cannot succeed AND costs a wasted vendor lookup.
      waitStartedAtRef.current = Date.now()
      attemptRef.current = 0
      update({ phase: 'waiting', attempt: 0 })
      void poll()
    },
    [poll, update]
  )

  const onWidgetMalformed = useCallback(() => {
    // Only meaningful while the widget is on screen; it is a hint, never a state change.
    if (!completionSeenRef.current && sessionRef.current) setMalformedSeen(true)
  }, [])

  const onWidgetConfigError = useCallback(() => {
    if (completionSeenRef.current || pendingRef.current || inFlightRef.current || !sessionRef.current) {
      return
    }

    // This handoff reached a widget that could not configure itself; never reuse it.
    sessionRef.current = null
    update({ phase: 'error', error: { kind: 'widget-config' } })
  }, [update])

  const onWidgetClose = useCallback(() => {
    // A close that follows a completion is just the dialog going away.
    if (completionSeenRef.current || inFlightRef.current || !sessionRef.current) return
    update({ phase: 'closed' })
  }, [update])

  /** Re-send the SAME request with the SAME Idempotency-Key. Never relaunches the widget. */
  const retry = useCallback(() => {
    const pending = pendingRef.current
    if (!pending || inFlightRef.current) return
    void submit(pending)
  }, [submit])

  return {
    state,
    start,
    retry,
    onWidgetComplete,
    onWidgetClose,
    onWidgetMalformed,
    onWidgetConfigError,
    malformedSeen,
    canStart: Boolean(recordSubjectPrincipalId),
  }
}
